// components/Drafts.tsx
// View and manage saved drafts

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useDrafts } from '../firebase/useDrafts';
import './ContentLibrary.css';

interface DraftItem {
    id: string;
    type: string;
    title: string;
    lastSaved: Date;
}

export const Drafts: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const { deleteDraft: deleteDraftHook } = useDrafts('video');

    const [drafts, setDrafts] = useState<DraftItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');


    const loadDrafts = useCallback(async () => {
        if (!currentUser?.email) return;

        setLoading(true);
        try {
            const q = query(
                collection(db, 'barkBoard', 'drafts', 'items'),
                where('authorEmail', '==', currentUser.email)
            );

            const snapshot = await getDocs(q);
            const loadedDrafts = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    type: data.type,
                    title: data.title || 'Untitled Draft',
                    lastSaved: data.lastSaved?.toDate() || new Date(),
                };
            });

            setDrafts(loadedDrafts);
        } catch (err) {
            console.error('Failed to load drafts:', err);
            setError('Failed to load drafts');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    const handleResume = (draft: DraftItem) => {
        const routes = {
            video: '/upload-video',
            audio: '/upload-audio',
            article: '/create-article',
        };
        navigate(`${routes[draft.type as keyof typeof routes]}?draft=${draft.id}`);
    };

    const handleDelete = async (draftId: string) => {
        if (!window.confirm('Delete this draft?')) return;

        try {
            await deleteDraftHook(draftId);
            setDrafts(drafts.filter(d => d.id !== draftId));
        } catch (err) {
            alert('Failed to delete draft');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    return (
        <div className="library-container">
            <header className="library-header">
                <div className="header-content">
                    <div className="header-left">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            ← Back
                        </button>
                        <h1>My Drafts</h1>
                    </div>
                    <div className="header-actions">
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="library-content">
                {error && (
                    <div className="error-message">{error}</div>
                )}

                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading drafts...</p>
                    </div>
                ) : drafts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📝</div>
                        <h3>No drafts yet</h3>
                        <p>Drafts are automatically saved as you work</p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="create-button"
                        >
                            Create Content
                        </button>
                    </div>
                ) : (
                    <div className="posts-grid">
                        {drafts.map(draft => (
                            <div key={draft.id} className="post-card">
                                <div className="post-card-content">
                                    <h3 className="post-title">{draft.title}</h3>
                                    <div className="post-meta">
                                        <div className="meta-item">
                                            <span className="meta-icon">
                                                {draft.type === 'video' ? '🎥' :
                                                    draft.type === 'audio' ? '🎵' : '📝'}
                                            </span>
                                            <span className="meta-text">{draft.type}</span>
                                        </div>
                                        <div className="meta-item">
                                            <span className="meta-icon">📅</span>
                                            <span className="meta-text">{formatDate(draft.lastSaved)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="post-card-actions">
                                    <button
                                        onClick={() => handleResume(draft)}
                                        className="action-button edit-button"
                                    >
                                        📝 Resume
                                    </button>
                                    <button
                                        onClick={() => handleDelete(draft.id)}
                                        className="action-button delete-button"
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};